import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from '@tiptap/suggestion'
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from 'react'
import { Button } from '@opallabs/lapidary'
import * as styles from './styles'

export { createMentionListRenderer } from './renderer'

export interface MentionItem {
  id: string
  label: string
  document_resource_type?: string
  document_resource_id?: string
}

export interface MentionableResource extends MentionItem {
  resource_type: 'user' | 'group'
  document_resource_type: string
  document_resource_id: string
}

export interface MentionListProps
  extends SuggestionProps<MentionableResource, MentionItem> {
  onOpenChange: (open: boolean) => void
  open: boolean
}

export interface MentionListHandle {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean
}

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  ({ items, command, open, onOpenChange }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = (index: number) => {
      const item = items[index]

      if (item) {
        // Note the concatenation here of <id>:<type>. This is because Tiptap doesn't
        // have a way of storing additional properties on the model, and the backend
        // needs to know which resource type the ID belongs to. It gets substringed out
        // later when constructing a flattened comment string.
        command({
          id: `${item.id}:${item.resource_type}:${item.document_resource_id}:${item.document_resource_type}`,
          label: item.label,
        })
      }
    }

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length)
    }

    const downHandler = () => {
      const nextIndex = (selectedIndex + 1) % items.length
      setSelectedIndex(nextIndex)
    }

    const enterHandler = () => {
      // Select if valid item, otherwise close list
      if (
        items.length > 0 &&
        selectedIndex >= 0 &&
        selectedIndex < items.length
      ) {
        selectItem(selectedIndex)
        return true
      }
      return false
    }

    useLayoutEffect(() => {
      setSelectedIndex(0)
    }, [items])

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }

        if (event.key === 'ArrowDown' || event.key === 'Tab') {
          downHandler()
          return true
        }

        if (event.key === 'Escape') {
          onOpenChange(false)
          return true
        }

        if (event.key === 'Enter') {
          return enterHandler()
        }

        return false
      },
    }))

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.stopPropagation()}
          css={styles.dropdownMenu}
        >
          <VisuallyHidden asChild>
            <Dialog.Title>Select a person to mention</Dialog.Title>
          </VisuallyHidden>
          {items.length ? (
            items.map((item, index) => (
              <Button
                css={styles.item(index === selectedIndex)}
                ariaSelected={index === selectedIndex}
                name={item.label}
                buttonText={item.label}
                key={item.id}
                variant="ghost"
                onClick={() => {
                  selectItem(index)
                }}
              />
            ))
          ) : (
            <div css={styles.emptySuggestion}>
              This person doesn&apos;t appear to be in Opal or this workspace
            </div>
          )}
        </Dialog.Content>
      </Dialog.Root>
    )
  }
)

MentionList.displayName = 'MentionList'
